import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, CircleDot, Layers3, Move3d, ShieldCheck, Sparkles } from "lucide-react";
import { useRef, useState } from "react";
import type { CSSProperties } from "react";
import { apps, studio } from "../content/apps.mjs";
import { AnimatedText } from "./components/AnimatedText";
import { FadeIn } from "./components/FadeIn";
import { Magnet } from "./components/Magnet";
import { Marquee } from "./components/Marquee";

const principles = [
  {
    number: "01",
    title: "Focused products",
    body: "One clear problem, solved end to end. Every screen earns its place.",
    icon: CircleDot,
  },
  {
    number: "02",
    title: "Quietly expressive",
    body: "Motion, haptics, and typography guide the experience without getting in the way.",
    icon: Move3d,
  },
  {
    number: "03",
    title: "Privacy-minded",
    body: "We prefer on-device processing and collect only what a product truly needs.",
    icon: ShieldCheck,
  },
];

function BrandLockup({ footer = false }: { footer?: boolean }) {
  return (
    <span className={footer ? "brand-lockup footer-wordmark" : "brand-lockup"}>
      <img src={studio.mark} alt="" />
      <span>VIIS LABS</span>
    </span>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <a className="wordmark" href="#top" aria-label="Viis Labs home"><BrandLockup /></a>
      <button className="menu-toggle" aria-expanded={open} aria-controls="primary-navigation" onClick={() => setOpen(!open)}>
        {open ? "Close" : "Menu"}
      </button>
      <nav id="primary-navigation" className={open ? "nav-links is-open" : "nav-links"} aria-label="Primary navigation">
        <a href="#studio" onClick={() => setOpen(false)}>Studio</a>
        <a href="#apps" onClick={() => setOpen(false)}>Apps</a>
        <a href="#approach" onClick={() => setOpen(false)}>Approach</a>
      </nav>
      <a className="header-cta" href="#apps">Explore our apps <ArrowDownRight size={17} /></a>
    </header>
  );
}

function HeroVisual() {
  const reduceMotion = useReducedMotion();
  return (
    <Magnet className="hero-object-wrap">
      <motion.div
        className="hero-object"
        initial={{ opacity: 0, scale: 0.72, rotate: -7 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.div
          className="orbital orbital-one"
          animate={reduceMotion ? undefined : { rotate: 360 }}
          transition={{ duration: 22, ease: "linear", repeat: Infinity }}
        />
        <motion.div
          className="orbital orbital-two"
          animate={reduceMotion ? undefined : { rotate: -360 }}
          transition={{ duration: 31, ease: "linear", repeat: Infinity }}
        />
        <div className="brand-stage">
          <img src={studio.mark} alt="Viis Labs VL monogram" />
          <div className="brand-aura" />
        </div>
        <span className="orbit-label label-one">IMAGINE</span>
        <span className="orbit-label label-two">REFINE</span>
        <span className="orbit-label label-three">RELEASE</span>
      </motion.div>
    </Magnet>
  );
}

function AppCard({ app }: { app: (typeof apps)[number] }) {
  return (
    <article className="app-card" style={{ "--app-accent": app.accent } as CSSProperties}>
      <div className="app-card-copy">
        <div className="app-meta"><span>{app.status}</span><span>{app.category}</span></div>
        <div>
          <p className="app-eyebrow">{app.eyebrow}</p>
          <h3>{app.name}</h3>
          <p className="app-description">{app.description}</p>
        </div>
        <ul className="highlight-list">
          {app.highlights.map((item: string) => <li key={item}><Sparkles size={15} /> {item}</li>)}
        </ul>
        <a className="pill-link app-detail-link" href={app.routes.details}>Open app details <ArrowUpRight size={18} /></a>
      </div>
      <div className="app-card-visual">
        <div className="app-visual-orbit orbit-a" />
        <div className="app-visual-orbit orbit-b" />
        <div className="collection-icon-stage">
          <img className="collection-app-icon" src={app.icon} alt={`${app.name} app icon`} />
        </div>
        <span className="visual-caption">A VIIS LABS RELEASE</span>
      </div>
    </article>
  );
}

function App() {
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const objectY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <div id="top" className="site-shell">
      <Header />
      <main>
        <section className="hero" ref={heroRef} aria-labelledby="hero-title">
          <div className="hero-kicker"><span>Independent app studio</span><span>Independent · Available everywhere</span></div>
          <motion.div className="hero-title-wrap" style={{ y: titleY }}>
            <h1 id="hero-title" className="hero-title"><AnimatedText text="VIIS LABS" /></h1>
          </motion.div>
          <motion.div style={{ y: objectY }} className="hero-visual-position"><HeroVisual /></motion.div>
          <motion.p
            className="hero-copy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            {studio.tagline}<br />Small software with a point of view.
          </motion.p>
          <motion.a
            className="scroll-prompt"
            href="#studio"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            Meet the studio <ArrowDownRight size={17} />
          </motion.a>
        </section>

        <Marquee />

        <section className="section about" id="studio" aria-labelledby="about-title">
          <FadeIn className="section-label"><span>01</span> About the studio</FadeIn>
          <div className="about-grid">
            <FadeIn><h2 id="about-title" className="section-title">We make useful things feel <em>remarkable.</em></h2></FadeIn>
            <FadeIn className="about-copy" delay={0.12}>
              <p>{studio.description}</p>
              <p>Every Viis Labs release begins with one problem worth solving. We take it from first interaction to final detail, then keep refining after it reaches people.</p>
              <a className="text-link" href="#apps">Explore the collection <ArrowDownRight size={18} /></a>
            </FadeIn>
          </div>
        </section>

        <section className="section principles" id="approach" aria-labelledby="principles-title">
          <FadeIn className="section-label light"><span>02</span> How we work</FadeIn>
          <FadeIn><h2 id="principles-title" className="section-title light">Built around the details<br />that build <em>trust.</em></h2></FadeIn>
          <div className="principle-list">
            {principles.map((item, index) => {
              const Icon = item.icon;
              return (
                <FadeIn className="principle-row" delay={index * 0.06} key={item.number}>
                  <span className="principle-number">{item.number}</span>
                  <div className="principle-icon"><Icon size={25} strokeWidth={1.5} /></div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  <ArrowUpRight className="principle-arrow" size={22} />
                </FadeIn>
              );
            })}
          </div>
        </section>

        <section className="section projects" id="apps" aria-labelledby="apps-title">
          <FadeIn className="section-label"><span>03</span> App collection</FadeIn>
          <div className="projects-heading">
            <FadeIn><h2 id="apps-title" className="section-title">Small apps.<br /><em>Clear purpose.</em></h2></FadeIn>
            <FadeIn delay={0.1}><p>Each app gets its own identity, product story, support, and policies. The common thread is care.</p></FadeIn>
          </div>

          {apps.map((app) => <AppCard app={app} key={app.slug} />)}

          <div className="future-grid" aria-label="Future Viis Labs projects">
            <FadeIn className="future-card"><Layers3 size={28} /><span>Next release</span><strong>In the lab</strong><p>A new focused experience is taking shape.</p></FadeIn>
            <FadeIn className="future-card future-card-accent" delay={0.08}><Sparkles size={28} /><span>Our rhythm</span><strong>One at a time</strong><p>We give every idea the space to become a finished product.</p></FadeIn>
          </div>
        </section>

        <section className="closing" aria-labelledby="closing-title">
          <FadeIn className="closing-orbit"><div className="mini-orbit brand-mini-orbit"><img src={studio.mark} alt="" /></div></FadeIn>
          <FadeIn><p>Made independently. Designed intentionally.</p></FadeIn>
          <FadeIn delay={0.06}><h2 id="closing-title">SMALL APPS.<br /><em>BIG CARE.</em></h2></FadeIn>
          <FadeIn delay={0.12}><a className="closing-link" href="#apps">Explore the collection <ArrowUpRight size={21} /></a></FadeIn>
        </section>
      </main>

      <footer>
        <a className="wordmark" href="#top"><BrandLockup footer /></a>
        <p>Independent apps, crafted with care.</p>
        <div><a href="#studio">Studio</a><a href="#apps">Apps</a><a href="#approach">Approach</a></div>
        <span>© {new Date().getFullYear()} Viis Labs</span>
      </footer>
    </div>
  );
}

export default App;
